import type { FC, ReactNode } from 'react'
import type { ContentBlock, MediaSlot as MediaSlotRow, Settings } from '@/types/content'

import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { CtaButton } from '@/components/generals/cta-button'
import { MediaSlot } from '@/components/generals/media-slot'
import { SectionIntro } from '@/components/generals/section-intro'
import { cms, hasValue } from '@/lib/cms'
import { reveal } from '@/lib/reveal'

interface ClosingCtaProps {
    settings: Settings
    block: ContentBlock | null
    image: MediaSlotRow | null
}

// Shared final call to action at the end of every page.
export const ClosingCta: FC<ClosingCtaProps> = ({ settings, block, image }): ReactNode => {
    if (!block) return null

    return (
        <section id='abschluss' aria-labelledby='abschluss-titel' className='relative overflow-hidden bg-ink text-bone'>
            <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(80%_60%_at_15%_100%,rgba(222,116,72,0.16),transparent_65%)]' />
            <div aria-hidden='true' className='grain-layer' />

            <Container className='relative grid gap-14 py-24 sm:py-32 lg:grid-cols-12 lg:items-end lg:gap-10 lg:py-40'>
                <div className='flex flex-col gap-10 lg:col-span-7'>
                    <SectionIntro
                        block={block}
                        size='xl'
                        tone='dark'
                        headingId='abschluss-titel'
                        textClassName='max-w-lg'
                    />

                    <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center [&>*]:w-full sm:[&>*]:w-auto' {...reveal(200)}>
                        <CtaButton settings={settings} tone='dark' size='lg' />

                        {hasValue(block.button_text) && hasValue(block.button_link) && (
                            <ButtonLink
                                href={block.button_link}
                                label={block.button_text}
                                labelCms={cms('inhalte', block.id, 'button_text')}
                                variant='secondary'
                                tone='dark'
                                size='lg'
                            />
                        )}
                    </div>
                </div>

                <div className='lg:col-span-4 lg:col-start-9' {...reveal(120)}>
                    <MediaSlot
                        slot={image}
                        slotKey='LIFESTYLE_IMAGE_01'
                        className='aspect-[4/3] w-full lg:aspect-[3/4]'
                        tone='dark'
                        showCaption
                    />
                </div>
            </Container>
        </section>
    )
}
